/**
 * ONE Profile drawer
 *
 * Reshapes the stacked-sections profile into the shared `@tiaki/profile-ui`
 * tabbed shell so ONE presents the same Profile UX as AIM and SMS:
 * Personal | Assignments | Settings + a destructive Sign Out
 * footer. This file is the ONE ADAPTER — the shell + sections are
 * presentational, so ONE's hooks (auth-identity user, `useMyStudents`, the
 * me/contact query) are wired inside each tab component and injected via the
 * shell's `render()` slots.
 *
 * Tab contents:
 *   - Personal    → identity header + read-only `ProfilePersonalSection`
 *                   (ONE has no user-update endpoint wired; see the tab).
 *   - Assignments → ONE's linked-students surface, re-hosted unchanged.
 *   - Settings    → language preference + 403-gated notification
 *                   destinations (`ProfileContactAdapter`).
 *
 * Icons are component REFS, not JSX elements — `TabMenu.renderIcon` renders
 * refs at `size={20}`, matching AIM/SMS tab headings exactly.
 * ONE has no sub-drawer swap, so the shell stays uncontrolled (it holds the
 * active tab itself).
 */
import { useTranslation } from 'react-i18next';
import {
  HiOutlineUserCircle,
  HiOutlineClipboardDocumentCheck,
  HiOutlineCog6Tooth,
} from 'react-icons/hi2';
import { ProfileDrawerShell, type ProfileTab } from '@tiaki/profile-ui';
import { useAuthActions, type AllauthUser } from '@aegix/auth-identity';
import { deregisterForPush } from '@/push/registration';
import { ProfilePersonalTab } from './profile/ProfilePersonalTab';
import { ProfileAssignmentsTab } from './profile/ProfileAssignmentsTab';
import { ProfileSettingsTab } from './profile/ProfileSettingsTab';

interface ProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProfileDrawer({ isOpen, onClose }: ProfileDrawerProps) {
  const { t } = useTranslation();
  const { logout } = useAuthActions<AllauthUser>();

  const handleSignOut = () => {
    // Same wiring as the header user-menu's Logout row: auth-identity's
    // state machine drops to anonymous and the route guards redirect.
    onClose();
    // Drop this device's FCM registration BEFORE the session is
    // torn down — the deregister endpoint needs an authenticated call, and
    // ONE's allauth logout performs no server-side FCM deactivation (AIM's
    // signal path is specific to its legacy JWT `logout/` endpoint).
    // `deregisterForPush` never throws by contract; the catch keeps a
    // hypothetical rejection from surfacing as unhandled, and `finally`
    // guarantees logout runs regardless.
    void deregisterForPush()
      .catch(() => undefined)
      .finally(() => {
        void logout();
      });
  };

  const tabs: ProfileTab[] = [
    {
      value: 'personal',
      label: t('profile.tabPersonal'),
      icon: HiOutlineUserCircle,
      'data-e2e': 'profileTabPersonal',
      render: () => <ProfilePersonalTab />,
    },
    {
      value: 'assignments',
      label: t('profile.tabAssignments'),
      icon: HiOutlineClipboardDocumentCheck,
      'data-e2e': 'profileTabAssignments',
      render: () => <ProfileAssignmentsTab />,
    },
    {
      value: 'notifications',
      label: t('profile.tabSettings'),
      icon: HiOutlineCog6Tooth,
      'data-e2e': 'profileTabNotifications',
      render: () => <ProfileSettingsTab />,
    },
  ];

  return (
    <ProfileDrawerShell
      isOpen={isOpen}
      onClose={onClose}
      tabs={tabs}
      onSignOut={handleSignOut}
      title={t('profile.title')}
      signOutLabel={t('profile.signOut')}
      e2eTitle="profileDrawer"
    />
  );
}
